// Library imports
import React, { useState } from "react";

// User Component imports
import { Row, Text, TextInput } from "_core/components/common/UI";
import OpsDialog from "../common/OpsDialog";
import DemoReloadField from "./DemoReloadField";
import { DEMO_TITLE_MAX_LENGTH, canSaveDemo, reloadFormState, reloadSecToSend } from "../utils/demoPage";

// 未保存の入力を失わないよう、背景クリックでは閉じない
const IgnoreBackdropClick = () => {};

/**
 * デモページの設定変更（ページタイトル・自動リロード）と削除。ページ名（URL）は変えられない
 * 手で書き換えてロックされたページは閲覧だけにする
 */
const DemoEditModal = ({ page, isSaving, onClose, onSave, onDelete }) => {
    const initialReload = reloadFormState(page.reload_sec);
    const [title, setTitle] = useState(page.title);
    const [isAutoReload, setIsAutoReload] = useState(initialReload.isAutoReload);
    const [reloadSec, setReloadSec] = useState(initialReload.reloadSec);
    const [deleteAsk, setDeleteAsk] = useState(false);
    const isLocked = page.is_locked;
    const disabled = isSaving || isLocked;

    const trimmedTitle = title.trim();
    const isValid = trimmedTitle.length <= DEMO_TITLE_MAX_LENGTH && canSaveDemo(isAutoReload, reloadSec);
    const changed = isValid && (trimmedTitle !== page.title || reloadSecToSend(isAutoReload, reloadSec) !== page.reload_sec);
    const canSave = changed && !isSaving;

    const footer = isLocked ? (
        <button type="button" className="ops-footer-btn" onClick={onClose}>閉じる</button>
    ) : deleteAsk ? (
        <>
            <div className="ops-delete-confirm">
                <i className="bi bi-exclamation-triangle-fill" />
                <div>
                    <div className="ops-delete-confirm-title">このデモページを削除しますか？</div>
                    <div className="ops-delete-confirm-note">このURLにはアクセスできなくなり、元に戻せません。</div>
                </div>
            </div>
            <button type="button" className="ops-footer-btn ops-delete-cancel" onClick={() => setDeleteAsk(false)} disabled={isSaving}>やめる</button>
            <button type="button" className="ops-footer-btn is-danger" onClick={onDelete} disabled={isSaving}><i className="bi bi-trash3" /><span>削除する</span></button>
        </>
    ) : (
        <>
            <button type="button" className="ops-delete-button" aria-disabled={isSaving} title="このデモページを削除します"
                onClick={() => !isSaving && setDeleteAsk(true)}>
                <i className="bi bi-trash3" /><span>デモページを削除</span>
            </button>
            <div className="ops-footer-spacer" />
            <button type="button" className="ops-footer-btn" onClick={onClose} disabled={isSaving}>キャンセル</button>
            <button type="button" className="ops-footer-btn is-primary" disabled={!canSave}
                onClick={() => onSave(trimmedTitle, reloadSecToSend(isAutoReload, reloadSec))}>保存</button>
        </>
    );

    return (
        <OpsDialog show={true} className="ops-scope" title={isLocked ? "デモページの設定（ロック中）" : "デモページの設定変更"} subtitle={page.url} width={880} footer={footer}
            footerClassName={deleteAsk ? "is-delete-confirm" : ""}
            showClose={true} onClose={() => !isSaving && onClose()} onBackdropClick={isLocked ? undefined : IgnoreBackdropClick}>
            {isLocked &&
                <div className="ops-demo-lock-note">
                    <i className="bi bi-lock-fill" />
                    <div>
                        <div className="ops-demo-lock-note-title">手で書き換えられたページのため、設定変更・削除はできません</div>
                        <div className="ops-demo-lock-note-text">HTML の <code>&lt;meta name="prizmo-demo-lock"&gt;</code> を外すと、ここから変更・削除できるようになります（変更すると手で書き換えた内容は消えます）。</div>
                    </div>
                </div>
            }
            <div className="ops-demo-card is-dialog">
                <div className="ops-demo-row">
                    <div className="ops-demo-label">
                        <Text className="text-size-medium text-bold ops-demo-label-text">ページ名</Text>
                        <div className="ops-demo-label-hint">URL が変わるため変更できません</div>
                    </div>
                    <div className="ops-demo-form">
                        <div className="ops-demo-fixed-name">{page.page_name}</div>
                    </div>
                </div>
                <hr />

                <div className="ops-demo-row">
                    <div className="ops-demo-label">
                        <Text className="text-size-medium text-bold ops-demo-label-text">ページタイトル</Text>
                        <div className="ops-demo-label-hint">未入力時は既定値が使われます</div>
                    </div>
                    <div className="ops-demo-form">
                        <TextInput default={title} onchange={(e) => setTitle(e.target.value)}
                            placeholder="サンプル AIコンシェルジュ デモ" disabled={disabled}
                            rules={[{ type: "maxLength", value: DEMO_TITLE_MAX_LENGTH }]} />
                    </div>
                </div>
                <hr />

                <div className="ops-demo-row">
                    <div className="ops-demo-label">
                        <Row className="flex-left align-center spacing-4px">
                            <Text className="text-size-medium text-bold ops-demo-label-text">自動リロード</Text>
                            <Text className="ops-demo-required">※必須</Text>
                        </Row>
                        <div className="ops-demo-label-hint">無操作がこの時間続くとリロードします</div>
                    </div>
                    <DemoReloadField isAutoReload={isAutoReload} reloadSec={reloadSec} disabled={disabled}
                        onChangeAutoReload={setIsAutoReload} onChangeReloadSec={setReloadSec} />
                </div>
            </div>
        </OpsDialog>
    );
};

export default DemoEditModal;
